import React, { useEffect, useState } from 'react';
import { mfaService, MfaPolicy } from '../../services/mfaService';

const SecuritySettings: React.FC = () => {
  const [policy, setPolicy] = useState<MfaPolicy>('optional');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    mfaService
      .getPlatformPolicy()
      .then((data) => setPolicy(data.policy))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load platform MFA policy.' }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const data = await mfaService.updatePlatformPolicy(policy);
      setPolicy(data.policy);
      setMessage({ type: 'success', text: 'Platform MFA policy updated.' });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to save policy.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading security settings…</p>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Platform MFA policy</h2>
      <p className="text-sm text-gray-600 mb-6">
        Applies to platform users (admins and direct customers) who are not in an insurer organization.
        Insurer orgs manage their own MFA policy separately.
      </p>

      {message && (
        <div
          className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label htmlFor="platform-mfa-policy" className="block text-sm font-medium text-gray-700 mb-1">
            Requirement
          </label>
          <select
            id="platform-mfa-policy"
            value={policy}
            onChange={(e) => setPolicy(e.target.value as MfaPolicy)}
            className="block w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="optional">Optional — users may enable TOTP</option>
            <option value="mandatory">Mandatory — all platform users must enroll</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save policy'}
        </button>
      </form>
    </div>
  );
};

export default SecuritySettings;
